/**
 * Observability API module - cross-cutting platform observability surface:
 * cost, platform health, MTTR metrics, and automation rules.
 *
 * Real per-domain split: contains the actual implementations of the
 * observability domain API objects, extracted verbatim from the monolithic
 * frontend/src/services/api.ts as part of the ADR-0003 follow-up.
 *
 * Domain objects:
 *   - costApi              (cost summary + real-cost fetch per account)
 *   - platformHealthApi    (platform health probe)
 *   - mttrApi              (MTTR stats + history)
 *   - automationRulesApi   (automation rules CRUD/toggle/test/evaluate + executions/stats)
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';

// -- costApi --
export const costApi = {
  getSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cost/summary'));
  },
  fetchRealCost: async (accountId: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cost/fetch-real', { account_id: accountId }));
  },
};

// -- platformHealthApi --
export const platformHealthApi = {
  getHealth: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/platform/health'));
  },
};

// -- automationRulesApi --
export const automationRulesApi = {
  list: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/automation/rules'));
  },
  create: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/automation/rules', data));
  },
  get: async (ruleId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/automation/rules/${ruleId}`));
  },
  update: async (ruleId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/automation/rules/${ruleId}`, data));
  },
  delete: async (ruleId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/automation/rules/${ruleId}`));
  },
  toggle: async (ruleId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/automation/rules/${ruleId}/toggle`));
  },
  test: async (ruleId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/automation/rules/${ruleId}/test`));
  },
  evaluate: async (data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/automation/rules/evaluate', data || {}));
  },
  getExecutions: async (params?: { rule_id?: string; status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/automation/executions', params));
  },
  getStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/automation/stats'));
  },
};

// -- mttrApi --
export const mttrApi = {
  getStats: () => fetchClient.get('/api/v1/metrics/mttr').then(_thenUnwrap),
  getHistory: (params?: { days?: number }) => fetchClient.get('/api/v1/metrics/mttr/history', params).then(_thenUnwrap),
  // R166 — Wiz parity: issue-engine forecast (linear-regression projection
  // of open-issue count). BE existed at /api/v1/issues/forecast but no FE.
  getIssueForecast: (params?: { forecast_days?: number; severity?: string }) =>
    fetchClient.get('/api/v1/issues/forecast', params).then(_thenUnwrap),
  getIssuesMTTR: (params?: { severity?: string; days?: number }) =>
    fetchClient.get('/api/v1/issues/mttr', params).then(_thenUnwrap),
  getRiskByTeam: () => fetchClient.get('/api/v1/issues/risk-by-team').then(_thenUnwrap),
};
